import { z } from 'zod';

const envSchema = z.object({
  // API Configuration
  VITE_API_URL: z.string().url('VITE_API_URL must be a valid URL').default('http://localhost:3001'),

  // Environment
  NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),

  // Mode (Vite specific)
  MODE: z.string().default('development'),

  // Development mode flag
  DEV: z.boolean().default(true),

  // Production mode flag
  PROD: z.boolean().default(false),
});

export type EnvConfig = z.infer<typeof envSchema>;

function validateEnv(): EnvConfig {
  try {
    // Vite exposes environment variables through import.meta.env
    const envVars = {
      VITE_API_URL: import.meta.env.VITE_API_URL,
      NODE_ENV: import.meta.env.NODE_ENV,
      MODE: import.meta.env.MODE,
      DEV: import.meta.env.DEV,
      PROD: import.meta.env.PROD,
    };

    return envSchema.parse(envVars);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMessages = error.errors
        .map((err) => `${err.path.join('.')}: ${err.message}`)
        .join('\n');

      console.error(`Environment validation failed:\n${errorMessages}`);

      // In development, we can provide fallbacks
      if (import.meta.env.DEV) {
        console.warn('Using fallback environment values for development');
        return envSchema.parse({});
      }

      throw new Error(`Environment validation failed:\n${errorMessages}`);
    }
    throw error;
  }
}

// Export the validated environment
export const env = validateEnv();

// Type-safe environment access
export const config = {
  api: {
    baseUrl: env.VITE_API_URL,
  },
  app: {
    environment: env.NODE_ENV,
    isDevelopment: env.DEV,
    isProduction: env.PROD,
    mode: env.MODE,
  },
} as const;
